import api from './api';
import { Order } from '../types';

export interface PaymentIntentRequest {
  product_id: string;
  customer_email: string;
  customer_name: string;
}

export interface PaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
  amount: number;
  currency: string;
}

export interface CheckoutSessionResponse {
  success: boolean;
  session_id: string;
  checkout_url: string;
}

export interface AlipayPaymentRequest {
  product_id: string;
  amount: number;
  currency?: string;
  customer_email?: string;
  customer_name?: string;
}

export interface DownloadLinkResponse {
  download_url: string;
  expires_at: string;
}

class PaymentService {
  // 创建 Stripe Checkout 会话
  async createCheckoutSession(params: any) {
    // params: { line_items, success_url, cancel_url, payment_method }
    return api.post('/api/payments/create-checkout-session', params);
  }

  // 创建支付宝支付
  async createAlipayPayment(request: AlipayPaymentRequest): Promise<CheckoutSessionResponse> {
    const response = await api.post('/api/payments/create-alipay-payment', request);
    return response.data;
  }

  // 获取支付会话状态
  async getSessionStatus(sessionId: string) {
    const response = await api.get(`/api/payments/session/${sessionId}`);
    return response.data;
  }

  // 创建支付意图（用于嵌入式支付）
  async createPaymentIntent(request: PaymentIntentRequest): Promise<PaymentIntentResponse> {
    const response = await api.post('/api/payments/create-payment-intent', request);
    return response.data;
  }

  // 确认支付
  async confirmPayment(paymentIntentId: string, productId: string): Promise<Order> {
    const response = await api.post('/api/payments/confirm-payment', {
      payment_intent_id: paymentIntentId,
      product_id: productId,
    });
    return response.data;
  }

  // 获取订单信息
  async getOrder(orderId: string): Promise<Order> {
    const response = await api.get(`/api/orders/${orderId}`);
    return response.data;
  }

  // 获取下载链接
  async getDownloadLink(orderId: string): Promise<string> {
    const response = await api.get(`/api/orders/${orderId}/download`);
    return response.data.download_url;
  }

  // 验证支付状态
  async verifyPayment(paymentIntentId: string): Promise<{
    status: string;
    order_id?: string;
  }> {
    const response = await api.get(`/api/payments/verify/${paymentIntentId}`);
    return response.data;
  }

  // 重定向到 Stripe Checkout
  redirectToCheckout(checkoutUrl: string) {
    window.location.href = checkoutUrl;
  }

  // 处理支付成功回调
  async handlePaymentSuccess(sessionId: string) {
    try {
      const session = await this.getSessionStatus(sessionId);
      if (session.payment_status === 'complete') {
        // 支付成功，可以跳转到下载页面
        const productId = session.metadata?.product_id;
        if (productId) {
          window.location.href = `/download/${productId}`;
        }
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
    }
  }

  // 支付宝支付专用方法
  async payWithAlipay(productId: string, amount: number, customerEmail?: string, customerName?: string) {
    try {
      const response = await this.createAlipayPayment({
        product_id: productId,
        amount: amount,
        currency: 'cny',
        customer_email: customerEmail,
        customer_name: customerName
      });

      if (response.success && response.checkout_url) {
        // 重定向到支付宝支付页面
        this.redirectToCheckout(response.checkout_url);
        return response;
      } else {
        throw new Error('Failed to create Alipay payment');
      }
    } catch (error) {
      console.error('Alipay payment error:', error);
      throw error;
    }
  }
}

export const paymentService = new PaymentService(); 
